<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  getApplicationDocuments,
  useApplicationEditor,
  useApplicationParticipants,
  assignNotaryToApplication,
} from "@/entities/application";
import { extractItems } from "@/shared/serviceResponse";
import { getTemplateItem } from "@/entities/template";
import { toast } from "@/shared/store/alertStore";
import { convertNotaryToFields } from "@/shared/lib/notaryToFields";
import { updateMentionsInAllLanguages } from "@/shared/mention";
import { EnforcementInscriptionStatus } from "@/shared/config/enforcementInscriptionStatuses";
import { ParticipantRole } from "@/shared/config/participantRoles";
import { ensureStatusDictionary } from "@/shared/config/statusDictionary";

import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import Button from "@/shared/ui/button/Button.vue";
import {
  ActionBar,
  ActionBarContent,
  ActionBarBody,
  ActionBarSeparator,
  ActionBarClose,
} from "@/shared/ui/action-bar";
import { Spinner } from "@/shared/ui/spinner";
import { useConfirm } from "@/shared/ui/alert-dialog";
import { useSessionKeepAlive } from "@/shared/auth/useSessionKeepAlive";

import InscriptionRedactor from "./tabs/InscriptionRedactor.vue";
import InscriptionNotary from "./tabs/InscriptionNotary.vue";
import InscriptionAttachments from "./tabs/InscriptionAttachments.vue";
import StatusActionPicker from "./components/StatusActionPicker.vue";
import StepperTrigger from "./components/inscription-stepper/StepperTrigger.vue";
import StepperPanel from "./components/inscription-stepper/StepperPanel.vue";
import { useStepperState } from "./components/inscription-stepper/useStepperState.js";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const confirm = useConfirm();

const uuid = computed(() => route.params.id);
const editMode = computed(() => route.name === "client_application_edit");
const welcome = computed(() => route.query.welcome === "1");

const { form, loading, fetchApplication, updateApplication } =
  useApplicationEditor(uuid);

const { participants, fetchParticipants } = useApplicationParticipants(uuid);

const hasRecoverer = computed(() =>
  participants.value.some(
    (p) => p.participant_type_code === ParticipantRole.RECOVERER
  )
);

const template = ref(null);
const documents = ref([]);
const activeTab = ref(route.query.tab || "redactor");
const saving = ref(false);

// Вырезаем шумовые атрибуты (isLoading у mention, signatureVersion у signature),
// которые меняются без правки пользователя и иначе давали бы ложный dirty.
function normalizeDoc(value) {
  if (Array.isArray(value)) return value.map(normalizeDoc);
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      if (key === "attrs" && value.attrs && typeof value.attrs === "object") {
        // eslint-disable-next-line no-unused-vars
        const { isLoading, signatureVersion, ...rest } = value.attrs;
        out.attrs = rest;
      } else {
        out[key] = normalizeDoc(value[key]);
      }
    }
    return out;
  }
  return value;
}

function serializeContent() {
  return JSON.stringify({
    content: normalizeDoc(form.content ?? null),
    content_kz: normalizeDoc(form.content_kz ?? null),
    content_en: normalizeDoc(form.content_en ?? null),
  });
}

const savedContentSnapshot = ref(null);

function captureContentBaseline() {
  savedContentSnapshot.value = serializeContent();
}

const tabs = computed(() => ({
  redactor: {
    label: t("Редактор"),
    icon: "huge-eye",
    disabled: false,
  },
  attachments: {
    label: t("Вложения"),
    icon: "huge-file-attach",
    disabled: !doneEditor.value,
    disabledReason: t("Сначала заполните текст заявления"),
  },
  notary: {
    label: t("Подписание"),
    icon: "huge-user",
    disabled: !doneAttachments.value,
    disabledReason: t("Сначала прикрепите документы"),
  },
}));

const tabComponents = {
  redactor: InscriptionRedactor,
  attachments: InscriptionAttachments,
  notary: InscriptionNotary,
};

const selectedNotary = ref(null);
const assigning = ref(false);
const submittingToNotary = ref(false);

const DEFAULT_NOTARY_IDENTIFIER = "000000000000";
const assigned = computed(() => {
  const id = form.notary_identifier;
  return Boolean(id) && id !== DEFAULT_NOTARY_IDENTIFIER;
});

async function onAssignRandomNotary() {
  if (!uuid.value) return;
  if (assigning.value || assigned.value) return;
  if (!hasRecoverer.value) {
    toast.error(t("Сначала добавьте участника-исполнителя (RECOVERER)"));
    return;
  }

  assigning.value = true;
  try {
    const response = await assignNotaryToApplication(uuid.value, {
      notary_uuid: "",
    });
    const payload = response?.payload || response;
    const ok = response?.success !== false && payload?.ok !== false;
    if (!ok) {
      const message =
        typeof response?.error === "string"
          ? response.error
          : t("Не удалось назначить нотариуса");
      toast.error(message);
      return;
    }
    let info = payload?.notary_info;
    if (!info) {
      await fetchApplication(uuid.value, { silent: true });
      info = form.notary_info || null;
    } else {
      form.notary_info = info;
      form.notary_uuid = info.uuid;
      form.notary_identifier = info.identifier;
    }

    if (info) {
      selectedNotary.value = info;
      applyNotaryFieldsToForm(info);
      try {
        await updateApplication();
      } catch (e) {
        console.error("[onAssignRandomNotary] save document failed:", e);
        toast.error(t("Не удалось сохранить документ"));
      }
    }

    const fio =
      info &&
      ([info.last_name, info.first_name, info.middle_name]
        .filter(Boolean)
        .join(" ")
        .trim() ||
        info.identifier);
    toast.success(
      fio
        ? t("Нотариус автоматически выбран: ") + fio
        : t("Нотариус автоматически выбран")
    );
  } finally {
    assigning.value = false;
  }
}

async function onSelectNotary(notary) {
  if (!notary || !uuid.value) return;
  if (assigning.value) return;
  if (assigned.value) return;

  const isP2 = form.exec_inscription_type === "P2";
  if (isP2) {
    selectedNotary.value = notary;
    return;
  }

  assigning.value = true;
  try {
    const response = await assignNotaryToApplication(uuid.value, {
      notary_uuid: notary.uuid,
    });
    const payload = response?.payload || response;
    const ok = response?.success !== false && payload?.ok !== false;
    if (!ok) {
      const message =
        typeof response?.error === "string"
          ? response.error
          : t("Не удалось назначить нотариуса");
      toast.error(message);
      return;
    }
    const info = payload?.notary_info || notary;
    form.notary_info = info;
    form.notary_uuid = info.uuid || notary.uuid;
    form.notary_identifier = info.identifier || notary.identifier;
    selectedNotary.value = info;
    applyNotaryFieldsToForm(info);
    try {
      await updateApplication();
    } catch (e) {
      console.error("[onSelectNotary] save document after assign failed:", e);
      toast.error(t("Не удалось сохранить документ"));
    }
    toast.success(t("Нотариус назначен"));
  } finally {
    assigning.value = false;
  }
}

async function onSigned() {
  if (submittingToNotary.value) return;
  submittingToNotary.value = true;
  try {
    await fetchApplication(uuid.value, { silent: true });
    if (uuid.value) {
      router.replace({
        name: "client_application_view",
        params: { id: uuid.value },
      });
    }
  } finally {
    submittingToNotary.value = false;
  }
}

const currentTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

const isProject = computed(() => {
  const status = form.status;
  return !status || status === EnforcementInscriptionStatus.PROJECT;
});

const showSaveButton = computed(() => editMode.value && isProject.value);

const canSwitchMode = computed(() => editMode.value || isProject.value);

function toggleMode() {
  if (!uuid.value) return;
  const name = editMode.value
    ? "client_application_view"
    : "client_application_edit";
  router.replace({
    name,
    params: { id: uuid.value },
    query: { ...route.query },
  });
}

const contentSignature = computed(() => serializeContent());

const hasUnsavedChanges = computed(() => {
  if (savedContentSnapshot.value === null) return false;
  return contentSignature.value !== savedContentSnapshot.value;
});

const saveBarDismissed = ref(false);

const showSaveBar = computed(
  () =>
    editMode.value &&
    isProject.value &&
    hasUnsavedChanges.value &&
    !saveBarDismissed.value
);

watch(contentSignature, () => {
  saveBarDismissed.value = false;
});

function onSaveBarOpenChange(open) {
  if (!open) saveBarDismissed.value = true;
}

useSessionKeepAlive(hasUnsavedChanges);

async function changeTab(tab) {
  const def = tabs.value[tab];
  if (def?.disabled) {
    toast.error(def.disabledReason || t("Шаг недоступен"));
    return;
  }

  if (tab === "notary" && hasUnsavedChanges.value) {
    try {
      await confirm(
        t(
          "У вас есть несохранённые изменения. Сохранить перед переходом к подписанию?"
        ),
        t("Несохранённые изменения"),
        {
          confirmButtonText: t("Сохранить и продолжить"),
          cancelButtonText: t("Отмена"),
        }
      );
    } catch {
      return;
    }
    await handleSave();
  }

  activeTab.value = tab;
  router.replace({ query: { ...route.query, tab } });
}

const stepperOpen = ref(false);

const {
  steps,
  doneCount,
  totalSteps,
  progress,
  doneEditor,
  doneAttachments,
  goTo,
} = useStepperState({
  form,
  participants,
  documents,
  activeTab,
  changeTab,
});

function toggleStepper() {
  stepperOpen.value = !stepperOpen.value;
}

function closeStepper() {
  stepperOpen.value = false;
}

function onStepClick(step) {
  // goTo сам пропускает шаг редактора на «Вложения», если нет mentions.
  goTo(step);
}

async function fetchData() {
  if (!uuid.value) return;
  await fetchApplication(uuid.value);
  await Promise.all([
    refreshDocuments(),
    fetchTemplateItem(),
    fetchParticipants(),
  ]);
  await nextTick();
  captureContentBaseline();
}

async function refreshDocuments() {
  if (!uuid.value) return;
  const data = await getApplicationDocuments(uuid.value);
  if (data?.success) {
    documents.value = extractItems(data.payload);
  }
}

async function fetchTemplateItem() {
  const code = form.template_code;
  if (!code) return;
  const data = await getTemplateItem(code);
  if (data?.success && data.payload) {
    template.value = data.payload;
  }
}

function onUpdateContent({ lang, json }) {
  const key =
    lang === "kz" ? "content_kz" : lang === "en" ? "content_en" : "content";
  form[key] = json;
}

function applyNotaryFieldsToForm(notary) {
  if (!notary) return;
  const fields = convertNotaryToFields(notary);
  if (!fields.length) return;
  const updated = updateMentionsInAllLanguages(form, fields);
  ["content", "content_kz", "content_en"].forEach((key) => {
    if (updated[key] !== undefined) {
      form[key] = updated[key];
    }
  });
}

async function handleSave() {
  if (saving.value) return;
  saving.value = true;
  try {
    const payload = {
      content: form.content ?? {},
      content_kz: form.content_kz ?? {},
      content_en: form.content_en ?? {},
      languages: form.languages || ["ru", "kz"],
      contract_amount: form.contract_amount ?? 0,
      state_duty: form.state_duty ?? 0,
      status: form.status || EnforcementInscriptionStatus.PROJECT,
      description: form.description || "",
      note: form.note || "",
      fields: form.fields ?? {},
    };
    await updateApplication(payload);
    await fetchApplication(uuid.value);
    await nextTick();
    captureContentBaseline();
  } finally {
    saving.value = false;
  }
}

watch(
  () => route.query.tab,
  (val) => {
    if (val && tabs.value[val]) {
      activeTab.value = val;
    }
  }
);

onMounted(async () => {
  ensureStatusDictionary();
  await fetchData();
  if (form.notary_info) {
    applyNotaryFieldsToForm(form.notary_info);
    await nextTick();
    captureContentBaseline();
  }
});
</script>

<template>
  <div class="flex flex-col gap-6 z-1000 w-full h-full">
    <LoadingOverlay v-if="loading" :value="loading" />

    <div v-else class="flex w-full items-start justify-center">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div class="flex items-center gap-3">
            <StepperTrigger
              :open="stepperOpen"
              :done-count="doneCount"
              :total-steps="totalSteps"
              :progress="progress"
              @toggle="toggleStepper"
            />
            <StatusActionPicker
              v-if="uuid"
              :transaction-id="uuid"
              :current-status="form.status"
              @applied="fetchData"
            />

            <el-tooltip
              :content="
                editMode ? $t('Режим редактирования') : $t('Режим просмотра')
              "
              placement="top"
            >
              <Button
                v-if="canSwitchMode"
                variant="outline"
                @click="toggleMode"
              >
                <svg-icon
                  :name="editMode ? 'huge-eye' : 'huge-edit'"
                  class="w-4 h-4"
                />
              </Button>
            </el-tooltip>

            <Button
              v-if="showSaveButton"
              variant="default"
              :disabled="saving"
              @click="handleSave"
              class="9"
            >
              {{ $t("Сохранить") }}
            </Button>
          </div>
        </template>
      </BaseTabs>
    </div>

    <div v-if="!loading" class="relative flex w-full flex-1 min-h-0">
      <StepperPanel
        :open="stepperOpen"
        :steps="steps"
        :done-count="doneCount"
        :total-steps="totalSteps"
        :progress="progress"
        class="stepper-panel-overlay"
        @close="closeStepper"
        @step-click="onStepClick"
      />

      <component
        :is="currentTabComponent"
        v-if="currentTabComponent"
        :form="form"
        :uuid="uuid"
        :template="template"
        :documents="documents"
        :editable="editMode && isProject"
        :assigning="assigning"
        :assigned="assigned"
        :has-recoverer="hasRecoverer"
        :welcome="welcome"
        class="flex-1 min-w-0 h-full"
        @refresh-documents="refreshDocuments"
        @refresh="fetchData"
        @refresh-participants="fetchParticipants"
        @update:content="onUpdateContent"
        @change:tab="changeTab"
        @select-notary="onSelectNotary"
        @assign-random-notary="onAssignRandomNotary"
        @signed="onSigned"
      />
    </div>

    <ActionBar
      :open="showSaveBar"
      :close-on-escape="false"
      @update:open="onSaveBarOpenChange"
    >
      <ActionBarContent
        :aria-label="$t('Несохранённые изменения')"
        positioner-class="z-[110]"
      >
        <div class="flex items-center gap-2 pl-1 text-sm font-medium">
          <div class="size-2 shrink-0 rounded-full bg-amber-400" />
          {{ $t("Несохранённые изменения") }}
        </div>
        <ActionBarSeparator />
        <ActionBarBody>
          <Button
            variant="default"
            size="sm"
            :disabled="saving"
            @click="handleSave"
          >
            <Spinner v-if="saving" size="sm" class="mr-1.5 text-current" />
            <svg-icon
              v-else
              name="sli-disk"
              class="mr-1"
              style="width: 13px; height: 13px; fill: none"
            />
            {{ saving ? $t("Сохраняем…") : $t("Сохранить") }}
          </Button>
        </ActionBarBody>
        <ActionBarSeparator />
        <ActionBarClose as-child>
          <Button variant="ghost" size="icon-sm">
            <svg-icon name="huge-close" class="w-4 h-4" />
          </Button>
        </ActionBarClose>
      </ActionBarContent>
    </ActionBar>
  </div>
</template>
